/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useSnackbar } from "notistack";
import athenaClient from "lib/axios/clients";
import { REACT_APP_BASE_URL } from "resources/constants";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
  addScenarioTotals,
  setSelectedScenario,
  setCacheScenario,
} from "app/scenarios/duck/slices";
import { handleScenarioType } from "lib/utils/scenarios";
import ScenarioNameComponent from "app/scenarios/map/constraints/ScenarioNameComponent";
import _ from "lodash";
import mixpanelTrack from "app/common/MixpanelComponent";

const useStyles = makeStyles((theme) => ({
  circularProgress: {
    color: "white",
  },
  scenarioInfoRow: {
    display: "flex",
    alignItems: "center",
    "& :first-child": {
      marginRight: "10px",
    },
  },
  scenarioTitle: {
    marginBottom: "5px",
  },
  dialogTitle: {
    borderBottom: "1px solid #e2e2e2",
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
  },
  dialogContent: {
    padding: "25px",
  },
  dialogActions: {
    borderTop: "1px solid #e2e2e2",
  },
  sectionTitle: {
    margin: "10px 0px 10px 0px",
  },
  clonedScenarioInfo: {
    border: "1px solid #e2e2e2",
    borderRadius: "3px",
    padding: "10px",
  },
}));

const withEditScenario = (WrappedComponent, scenarioTotal) => {
  return (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [newScenarioName, setNewScenarioName] = React.useState("");
    const [newScenarioComment, setNewScenarioComment] = React.useState("");
    const [invalidName, setInvalidName] = React.useState(false);
    const [invalidcharacter, setInvalidcharacter] = React.useState(false);
    const [
      invalidcharacterInCommment,
      setInvalidcharacterInCommment,
    ] = React.useState(false);
    const scenariosSlice = useSelector((state) => state.scenarios);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const handleClickDialogOpen = (e) => {
      e.stopPropagation();
      setDialogOpen(true);
    };

    const handleDialogClose = (e) => {
      e.stopPropagation();
      setDialogOpen(false);
      if (props.onDialogClose) props.onDialogClose(e);
    };

    const loadingCircle = () => (
      <CircularProgress size={24} className={classes.circularProgress} />
    );

    const editScenario = async () => {
      const config = {
        method: "PUT",
        url: `${REACT_APP_BASE_URL}/scenarios/${scenarioTotal.scenario.id}`,
        data: {
          id: scenarioTotal.scenario.id,
          name: newScenarioName,
          comment: newScenarioComment,
        },
      };

      await athenaClient(config);

      const editedScenario = {
        ...scenarioTotal,
        scenario: {
          ...scenarioTotal.scenario,
          name: newScenarioName,
          comment: newScenarioComment,
        },
      };

      dispatch(
        addScenarioTotals({ [editedScenario.scenario.id]: editedScenario })
      );
      dispatch(setSelectedScenario(editedScenario));
      dispatch(
        setCacheScenario({
          [handleScenarioType(editedScenario.scenario.cycle.cycle_type_id)]: {
            ...scenarioTotal.scenario,
            ...editedScenario,
          },
        })
      );
    };

    const handleClickConfirm = (e) => {
      /* MIXPANEL TRACKS */
      mixpanelTrack(`Edit scenario ${scenarioTotal.scenario.name}`);
      /* END OF MIXPANEL TRACKS */

      handleDialogClose(e);

      const snackbarKey = enqueueSnackbar(
        `'${scenarioTotal.scenario.name}' is getting edited`,
        {
          persist: true,
          action: loadingCircle,
        }
      );

      editScenario(scenarioTotal)
        .then(() => {
          enqueueSnackbar(
            `Scenario '${scenarioTotal.scenario.name}' successfully renamed to '${newScenarioName}'`,
            {
              variant: "success",
            }
          );
        })
        .catch(() => {
          enqueueSnackbar(
            `Error when editing '${scenarioTotal.scenario.name}'. Try again shortly`,
            {
              variant: "error",
            }
          );
        })
        .finally(() => {
          closeSnackbar(snackbarKey);
        });
    };

    useEffect(() => {
      if (newScenarioName) {
        const scenariosWithoutSelected = _.filter(
          scenariosSlice.totalsByScenarioId,
          (scenario) => {
            return scenario.scenario.name !== scenarioTotal.scenario.name;
          }
        );
        setNewScenarioName(newScenarioName.trimStart().replace("  ", " "));
        setInvalidName(
          _.find(scenariosWithoutSelected, [
            "scenario.name",
            newScenarioName.trim(),
          ])
        );
        setInvalidcharacter(
          !/^[A-Za-z0-9\sÁÉÍÓÚáéíóúÜüÑñ-]+$/.test(newScenarioName.trim())
        );
      }
    }, [newScenarioName]);

    useEffect(() => {
      if (newScenarioComment) {
        setInvalidcharacterInCommment(
          newScenarioComment.trim().includes("|") ||
            newScenarioComment.trim().includes(",")
        );
      }
    }, [newScenarioComment]);

    useEffect(() => {
      if (_.isEmpty(scenarioTotal)) return;
      setNewScenarioName(scenarioTotal.scenario.name);
      setNewScenarioComment(scenarioTotal.scenario.comment);
    }, [scenarioTotal]);

    const handleDialogClick = (e) => {
      /**
       * We need to stop the propagation of the click event when clicking
       * anywhere within the dialog window to prevent side effects
       */
      e.stopPropagation();
    };

    return (
      <>
        <WrappedComponent
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
          onClick={handleClickDialogOpen}
          onMouseDown={handleClickDialogOpen}
        />
        {!_.isEmpty(scenarioTotal) && (
          <Dialog
            open={dialogOpen}
            onClose={handleDialogClose}
            onClick={handleDialogClick}
          >
            <DialogTitle className={classes.dialogTitle}>
              Edit scenario
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              <ScenarioNameComponent
                scenarioTotal={scenarioTotal}
                newScenarioName={newScenarioName}
                setNewScenarioName={setNewScenarioName}
                newScenarioComment={newScenarioComment}
                setNewScenarioComment={setNewScenarioComment}
                invalidName={invalidName}
                invalidcharacter={invalidcharacter}
                invalidcharacterInCommment={invalidcharacterInCommment}
              />
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
              <Button autoFocus onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                disabled={
                  invalidName || invalidcharacter || invalidcharacterInCommment
                }
                variant="contained"
                onClick={handleClickConfirm}
                color="primary"
              >
                Accept
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  };
};

export default withEditScenario;
